/**
 * "Volgende stap" — één kaart bovenaan de rustige projectpagina, met één primaire knop.
 *
 * Pure functie: `bepaalVolgendeStap(stand)` loopt de regels hieronder in volgorde af; de eerste
 * die vuurt wint. Is die stap urgent (taken over datum, callsheet), dan komt de volgende regel
 * die vuurt erbij als "Daarna:". Elke stap draagt een `waarom` ("omdat …") voor de tooltip.
 * Onbekende data (nog aan het laden, lezen mislukt) laat een regel zwijgen; vuurt er niets,
 * dan volgt een neutrale stap.
 *
 * Een actie is data, geen functie: { soort, label, ...param }. De pagina vertaalt die:
 *   paneel  → paneel openen (taken, deliverables, …)    tool    → naar een tool met projectcontext
 *   fase    → werkvlak van een fase openen              opstart → opstart-wizard
 *   voorstel→ klaargezet taakvoorstel openen            briefing→ klantbriefing kiezen
 *   mail    → opvolgmail in je mailprogramma            gripp   → offerte/opdracht in Gripp
 *   taak    → één taak openen                          ververs → alles opnieuw laden
 */
import { korteDatum, dagEnDatum, dagenTot } from './datums';
import {
  openTaken, takenOverDatum, taakEind, taakEigenaar, draaidagStand, heeftCallsheet,
  callsheetNietNodig, deliverableNaam, deliverableStatus, geld,
} from './projectStand';

const meer = (n, enkel, meervoud) => `${n} ${n === 1 ? enkel : meervoud}`;
const opsomming = (delen) => (delen.length <= 1 ? delen.join('')
  : `${delen.slice(0, -1).join(', ')} en ${delen[delen.length - 1]}`);

const VROEGE_FASEN = new Set(['opstartfase', 'offertefase', 'concept', 'preproductie']);
const LATE_FASEN = new Set(['postproductie', 'afronding', 'evaluatie']);
const FEEDBACK_KLANT = new Set(['Feedback ontvangen klant', 'Feedback verwerken']);
const CALLSHEET_DAGEN = 7;

/** Regels in volgorde. Elke regel: (stand) => stap | null. */
export const REGELS = [
  // 1. Afgerond/geannuleerd: niets meer te doen behalve (eventueel) evalueren.
  function afgerond(s) {
    const status = String(s.projectStatus || '').toLowerCase();
    if (status !== 'afgerond' && status !== 'geannuleerd') return null;
    const evaluatie = (s.fasen || []).find((p) => p.phase_key === 'evaluatie' && p.is_enabled);
    if (status === 'afgerond' && evaluatie && evaluatie.status !== 'klaar') {
      return {
        regel: 'evaluatie', titel: 'Project evalueren',
        tekst: 'Het project is afgerond (gefactureerd). Rond de evaluatie af.',
        waarom: 'omdat de projectstatus Afgerond is en de fase Evaluatie nog niet klaar is',
        actie: { soort: 'fase', fase: 'evaluatie', label: 'Evaluatie openen' },
      };
    }
    return {
      regel: 'afgerond', neutraal: true,
      titel: status === 'afgerond' ? 'Project is afgerond' : 'Project is geannuleerd',
      tekst: 'Er staat niets meer open. Alles blijft hieronder als naslag beschikbaar.',
      waarom: `omdat de projectstatus ${s.projectStatus} is`,
      actie: { soort: 'paneel', paneel: 'activiteit', label: 'Activiteit bekijken' },
    };
  },

  // 2. Taken over datum (urgent).
  function takenTeLaat(s) {
    const laat = takenOverDatum(s.taken, s.vandaag);
    if (!laat.length) return null;
    const delen = laat.slice(0, 2).map((t) => `${t.fields?.NaamTaak || 'naamloze taak'} (${korteDatum(taakEind(t), s.vandaag)})`);
    if (laat.length > 2) delen.push(meer(laat.length - 2, 'andere', 'andere'));
    return {
      regel: 'taken_over_datum', urgent: true,
      titel: `${meer(laat.length, 'taak', 'taken')} over datum`,
      tekst: `${opsomming(delen)}.`,
      waarom: 'omdat de einddatum voorbij is en de taken nog open staan',
      actie: { soort: 'paneel', paneel: 'taken', label: 'Taken openen' },
    };
  },

  // 3. Draaidag binnen 7 dagen zonder callsheet (urgent).
  function callsheet(s) {
    const { komend } = draaidagStand(s.draaidagen, s.vandaag);
    const zonder = komend.find(({ d, dag }) => dagenTot(dag, s.vandaag) <= CALLSHEET_DAGEN
      && !heeftCallsheet(d) && !callsheetNietNodig(d));
    if (!zonder) return null;
    const n = dagenTot(zonder.dag, s.vandaag);
    const wanneer = n === 0 ? 'vandaag' : n === 1 ? 'morgen' : `over ${n} dagen`;
    return {
      regel: 'callsheet', urgent: true,
      titel: `Callsheet maken voor ${dagEnDatum(zonder.dag, s.vandaag)}`,
      tekst: `${zonder.d.fields?.Draaidag || 'De draaidag'} is ${wanneer} en heeft nog geen callsheet.`,
      kort: `callsheet voor de draaidag van ${dagEnDatum(zonder.dag, s.vandaag)}`,
      waarom: `omdat de draaidag ${wanneer} is (binnen ${CALLSHEET_DAGEN} dagen) en er nog geen callsheet aan hangt`,
      actie: { soort: 'tool', tool: 'callsheet', label: 'Callsheet maken' },
    };
  },

  // 4. Klantfeedback op een versie (V1/V2) → verwerken.
  function feedback(s) {
    const d = (s.deliverables || []).find((x) => FEEDBACK_KLANT.has(deliverableStatus(x)));
    if (!d) return null;
    return {
      regel: 'feedback', titel: `Feedback verwerken: ${deliverableNaam(d)}`,
      tekst: `De klant heeft gereageerd op ${deliverableNaam(d)} (status: ${deliverableStatus(d)}).`,
      kort: `feedback verwerken op ${deliverableNaam(d)}`,
      waarom: `omdat deliverable ${deliverableNaam(d)} op "${deliverableStatus(d)}" staat`,
      actie: { soort: 'paneel', paneel: 'deliverables', label: 'Deliverables openen' },
    };
  },

  // 5. Klaargezet taakvoorstel na acceptatie.
  function taakvoorstel(s) {
    if (!s.taakvoorstel) return null;
    const n = Number(s.taakvoorstel.task_count) || 0;
    return {
      regel: 'taakvoorstel', titel: 'Taakvoorstel bekijken',
      tekst: `${n ? meer(n, 'actiepunt', 'actiepunten') : 'Actiepunten'} uit de offerte staan klaar, nog niet ingepland.`,
      kort: 'het taakvoorstel uit de offerte bekijken',
      waarom: 'omdat er na acceptatie van de offerte een taakvoorstel is klaargezet dat nog niet is bekeken',
      actie: { soort: 'voorstel', label: 'Voorstel openen' },
    };
  },

  // 6. Offerte akkoord, nog geen taken → opstarten.
  function opstarten(s) {
    if (s.offerte?.stand !== 'akkoord') return null;
    if (!Array.isArray(s.taken) || s.taken.length > 0) return null;   // onbekend telt niet als "geen taken"
    if (LATE_FASEN.has(s.huidigeFase)) return null;
    const dels = s.deliverables || [];
    if (dels.length && dels.every((d) => deliverableStatus(d) === 'Opgeleverd')) return null;
    return {
      regel: 'opstarten', titel: 'Project opstarten',
      tekst: `De offerte${s.offerte.bedrag ? ` van ${geld(s.offerte.bedrag)}` : ''} is akkoord. Zet de taken, het team en de planning klaar.`,
      kort: 'het project opstarten',
      waarom: 'omdat de offerte akkoord is en er nog geen taken bij het project staan',
      actie: { soort: 'opstart', label: 'Project opstarten' },
    };
  },

  // 7. Offerte open → opvolgen.
  function offerteOpvolgen(s) {
    if (s.offerte?.stand !== 'open') return null;
    const zinnen = [`De offerte${s.offerte.bedrag ? ` van ${geld(s.offerte.bedrag)}` : ''} staat open${
      s.offerte.gewijzigd ? ` (laatst gewijzigd ${korteDatum(s.offerte.gewijzigd, s.vandaag)})` : ''}.`];
    if (s.mails?.laatsteDatum) {
      zinnen.push(`${s.mails.laatsteVanKlant ? 'De klant mailde' : 'Wij mailden'} voor het laatst op ${korteDatum(s.mails.laatsteDatum, s.vandaag)}.`);
    }
    return {
      regel: 'offerte_opvolgen', titel: s.klant ? `Offerte opvolgen bij ${s.klant}` : 'Offerte opvolgen',
      tekst: zinnen.join(' '),
      kort: 'de offerte opvolgen',
      waarom: 'omdat er een offerte in Gripp staat die nog niet is geaccepteerd',
      actie: { soort: 'mail', label: 'Opvolgmail opstellen' },
      secundair: { soort: 'gripp', label: 'Offerte in Gripp' },
    };
  },

  // 8. Nog geen offerte in de offerte-/opstartfase → offerte afronden.
  function offerteMaken(s) {
    if (s.offerte?.stand !== 'geen') return null;
    const vroeg = ['opstartfase', 'offertefase'].includes(s.huidigeFase)
      || String(s.projectStatus || '').toLowerCase() === 'offerte';
    if (!vroeg) return null;
    return {
      regel: 'offerte_maken', titel: 'Offerte afronden',
      tekst: 'Er hangt nog geen offerte aan dit project. Maak hem in de offertegenerator.',
      kort: 'de offerte afronden',
      waarom: 'omdat het project in de offertefase zit en er nog geen offerteregels aan hangen',
      actie: { soort: 'tool', tool: 'offerte', label: 'Offerte maken' },
    };
  },

  // 9. Briefing ontbreekt (vroege fasen).
  function briefing(s) {
    if (!Array.isArray(s.briefings) || s.briefings.length > 0) return null;
    if (!VROEGE_FASEN.has(s.huidigeFase)) return null;
    return {
      regel: 'briefing', titel: 'Klantbriefing uploaden',
      tekst: 'Er is nog geen briefing van de klant bewaard. Upload het document of plak de mail; daarna lezen we wat er ontbreekt.',
      kort: 'de klantbriefing uploaden',
      waarom: 'omdat er nog geen klantbriefing bij dit project is bewaard',
      actie: { soort: 'briefing', label: 'Klantbriefing toevoegen' },
    };
  },

  // 10. Wachten op interne feedback.
  function interneFeedback(s) {
    const d = (s.deliverables || []).find((x) => deliverableStatus(x) === 'Wachten op interne feedback');
    if (!d) return null;
    return {
      regel: 'interne_feedback', titel: `Interne feedback op ${deliverableNaam(d)}`,
      tekst: `${deliverableNaam(d)} wacht op interne feedback voordat hij naar de klant kan.`,
      kort: `interne feedback op ${deliverableNaam(d)}`,
      waarom: `omdat deliverable ${deliverableNaam(d)} op "Wachten op interne feedback" staat`,
      actie: { soort: 'paneel', paneel: 'deliverables', label: 'Deliverables openen' },
    };
  },

  // 11. De eerstvolgende open taak.
  function volgendeTaak(s) {
    const [t] = openTaken(s.taken);
    if (!t) return null;
    const dag = taakEind(t);
    const naam = t.fields?.NaamTaak || 'Naamloze taak';
    const eigenaar = taakEigenaar(t);
    return {
      regel: 'volgende_taak', neutraal: true, titel: naam,
      tekst: [dag ? `Uiterlijk ${korteDatum(dag, s.vandaag)}` : 'Nog zonder datum', eigenaar ? `bij ${eigenaar}` : 'nog zonder uitvoerder'].join(', ') + '.',
      kort: `${naam}${dag ? ` (${korteDatum(dag, s.vandaag)})` : ''}`,
      waarom: 'omdat dit de eerstvolgende open taak van het project is',
      actie: { soort: 'taak', taakId: t.id, label: 'Taak openen' },
    };
  },

  // 12. Alles opgeleverd, project nog niet afgerond.
  function afronden(s) {
    if (!Array.isArray(s.taken)) return null;   // taken onbekend: niet "alles af" roepen
    if (openTaken(s.taken).length) return null;  // er staat nog echt werk open
    const lijst = s.deliverables || [];
    if (!lijst.length || !lijst.every((d) => deliverableStatus(d) === 'Opgeleverd')) return null;
    return {
      regel: 'afronden', titel: 'Project afronden',
      tekst: `${lijst.length === 1 ? 'De deliverable is' : `Alle ${lijst.length} deliverables zijn`} opgeleverd. Factureren en archiveren gaat via Gripp.`,
      kort: 'het project afronden',
      waarom: 'omdat alle deliverables op Opgeleverd staan en het project nog niet is afgerond',
      actie: { soort: 'fase', fase: 'afronding', label: 'Afronding openen' },
    };
  },
];

/** Neutrale stap als geen regel vuurt. */
export function neutraleStap(s) {
  // Takenlijst niet geladen: niet geruststellen met "niets open", maar eerlijk zeggen.
  if (s.taken === null) {
    return {
      regel: 'taken_onbekend', neutraal: true,
      titel: 'Taken niet geladen',
      tekst: 'De takenlijst kon niet worden opgehaald, dus de volgende stap is nog onzeker.',
      waarom: 'omdat het ophalen van de taken mislukte; een lege lijst is hier geen "niets te doen"',
      actie: { soort: 'ververs', label: 'Opnieuw laden' },
    };
  }
  const fase = (s.fasen || []).find((p) => p.phase_key === s.huidigeFase);
  return {
    regel: 'neutraal', neutraal: true,
    titel: 'Geen openstaande stap',
    tekst: fase ? `Er staat niets open dat nu aandacht vraagt. Het project zit in ${fase.label}.` : 'Er staat niets open dat nu aandacht vraagt.',
    waarom: 'omdat geen van de regels iets vond; bij twijfel tonen we een neutrale stap',
    actie: fase
      ? { soort: 'fase', fase: fase.phase_key, label: `${fase.label} openen` }
      : { soort: 'paneel', paneel: 'activiteit', label: 'Activiteit bekijken' },
  };
}

/**
 * @param {object} stand
 *   vandaag, projectStatus, klant, fasen, huidigeFase (phase_key), offerte (offerteStand),
 *   taken, draaidagen, deliverables, briefings (array | null = onbekend),
 *   mails (mailStand | null), taakvoorstel
 * @returns {{regel, titel, tekst, waarom, actie, secundair?, daarna?, urgent?, neutraal?}}
 */
export function bepaalVolgendeStap(stand = {}) {
  const s = { vandaag: new Date(), ...stand };
  const treffers = [];
  for (const regel of REGELS) {
    let stap = null;
    try { stap = regel(s); } catch { stap = null; }   // een kapotte regel mag de kaart niet slopen
    if (stap) treffers.push(stap);
    if (treffers.length === 2) break;
  }
  const [eerste, tweede] = treffers;
  if (!eerste) return neutraleStap(s);
  // Afgerond/geannuleerd is een eindstand: geen tweede actie ernaast.
  if (eerste.regel === 'afgerond' || eerste.regel === 'evaluatie') return eerste;
  if (eerste.urgent && tweede && !tweede.neutraal) {
    return { ...eerste, daarna: { titel: tweede.kort || tweede.titel, actie: tweede.actie, waarom: tweede.waarom }, secundair: tweede.actie };
  }
  // Niet urgent: de tweede treffer mag de stille tekstlink zijn (bv. "Klantbriefing toevoegen"
  // naast "Opvolgmail opstellen"), zonder eigen tekst; anders de eigen tekstlink van de stap.
  if (tweede && !tweede.neutraal && tweede.actie.soort !== eerste.actie.soort) {
    return { ...eerste, secundair: tweede.actie };
  }
  return eerste;
}
